import type { Tutorial } from "@/lib/tutorials";
import type { CSSProperties } from "react";

import {
  Button,
  CloseButton,
  Link,
  Modal,
  Skeleton,
  Typography,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useState } from "react";

import { useAppTheme } from "@/hooks/use-app-theme";
import {
  ARCADE_EMBED_ASPECT,
  ARCADE_EMBED_CONTROL_BAR,
  embedUrl,
  findNextTutorial,
  tutorialLink,
  tutorialMeta,
} from "@/lib/tutorials";

interface TutorialPlayerProps {
  tutorial: Tutorial;
  onClose: () => void;
}

export const TutorialPlayer = ({
  tutorial: initialTutorial,
  onClose,
}: TutorialPlayerProps) => {
  const [tutorial, setTutorial] = useState(initialTutorial);
  const [isLoaded, setIsLoaded] = useState(false);
  const theme = useAppTheme();
  const paddingBottom = `calc(${(ARCADE_EMBED_ASPECT * 100).toFixed(4)}% + ${ARCADE_EMBED_CONTROL_BAR}px)`;
  const maxWidth = `min(1200px, calc((100dvh - 220px) / ${ARCADE_EMBED_ASPECT}))`;
  const meta = tutorialMeta(tutorial);
  const src = embedUrl(tutorial);
  const shareUrl = tutorialLink(tutorial);
  const next = findNextTutorial(tutorial);

  const playNext = () => {
    if (!next) return;
    setIsLoaded(false);
    setTutorial(next);
  };

  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Container className="p-0 sm:p-6" scroll="inside" size="lg">
        <Modal.Dialog
          className="h-full max-w-none border border-border p-4 shadow-none max-sm:rounded-none sm:h-auto sm:max-w-(--tutorial-player-width)"
          style={{ "--tutorial-player-width": maxWidth } as CSSProperties}
        >
          <CloseButton
            aria-label="Close tutorial"
            className="absolute right-4 top-4"
            onPress={onClose}
          />
          <Modal.Header className="gap-1 pr-10">
            <Modal.Heading>{tutorial.title}</Modal.Heading>
            <Typography color="muted" type="body-xs">
              {meta}
            </Typography>
          </Modal.Header>
          <Modal.Body className="flex items-center">
            <div className="relative h-0 w-full" style={{ paddingBottom }}>
              {!isLoaded && (
                <Skeleton
                  aria-label="Loading tutorial"
                  className="absolute inset-0 h-full w-full rounded-none"
                />
              )}
              <iframe
                key={tutorial.id}
                allowFullScreen
                allow="clipboard-write; autoplay"
                className="absolute inset-0 h-full w-full border-0"
                loading="lazy"
                src={src}
                style={{ colorScheme: theme }}
                title={tutorial.title}
                onLoad={() => setIsLoaded(true)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Link
              className="mr-auto text-sm"
              href={shareUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              Open in new tab
              <Icon
                aria-hidden="true"
                icon="solar:arrow-right-up-linear"
                width={16}
              />
            </Link>
            {next && (
              <Button
                className="h-auto min-h-8 whitespace-normal text-left"
                size="sm"
                variant="ghost"
                onPress={playNext}
              >
                Up next: {next.title}
              </Button>
            )}
            <Button size="sm" variant="secondary" onPress={onClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};
